/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/gatewayApi.ts
 🗓️ Created: 2025-01-17
 🧩 Description:
    API functions for Gateway operations.
    Centralized API calls for better maintainability.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { Gateway, GatewaySipProfile, GatewayPayload, GatewayFetchParams, GatewayFetchResponse, GatewayStatus } from '../types';

export const GATEWAYS_ENDPOINT = '/api/gateways';
export const GATEWAY_SIP_PROFILES_ENDPOINT = '/api/telephony/gateway-sip-profiles';
export const SIP_PROFILES_ENDPOINT = '/api/gateways/sip-profiles';

export interface ApiPagination {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
  pagination?: ApiPagination;
}

export interface ApiErrorResponse {
  success: false;
  message?: string;
  [key: string]: unknown;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse | null | undefined;

const normaliseHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  return headers;
};

export async function requestGatewaysApi<T>(
  endpoint: string,
  fallbackMessage: string,
  options: RequestInit = {}
): Promise<ApiSuccessResponse<T>> {
  const response = await fetch(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getApiHeaders(),
        ...normaliseHeaders(options.headers)
      }
    }
  );

  let payload: ApiResponse<T>;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (!payload || (typeof payload === 'object' && 'success' in payload && payload.success !== true)) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (payload && typeof payload === 'object' && 'success' in payload && payload.success) {
    return payload as ApiSuccessResponse<T>;
  }

  return {
    success: true,
    data: undefined as T
  };
}

/**
 * Fetch gateways list
 */
export async function fetchGateways(
  params: GatewayFetchParams,
  tenantSlug?: string,
  userId?: number
): Promise<GatewayFetchResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search ? { search: params.search } : {}),
    ...(params.domainId != null ? { domainId: params.domainId.toString() } : {})
  });

  const { data: rawData, pagination } = await requestGatewaysApi<Gateway[]>(
    `${GATEWAYS_ENDPOINT}?${queryParams.toString()}`,
    'Failed to fetch gateways',
    { headers: getApiHeaders(tenantSlug, userId) }
  );

  const data = Array.isArray(rawData) ? rawData : [];
  return {
    data,
    pagination: {
      page: pagination?.page ?? params.page,
      limit: pagination?.limit ?? params.limit,
      total: pagination?.total ?? data.length,
      totalPages: pagination?.totalPages ?? 0
    }
  };
}

/**
 * Fetch single gateway
 */
export async function fetchGateway(id: string): Promise<Gateway> {
  const { data } = await requestGatewaysApi<Gateway>(
    `${GATEWAYS_ENDPOINT}/${id}`,
    'Failed to fetch gateway'
  );
  return data;
}

/**
 * Create gateway
 */
export async function createGateway(payload: GatewayPayload): Promise<Gateway> {
  const { data } = await requestGatewaysApi<Gateway>(
    GATEWAYS_ENDPOINT,
    'Failed to create gateway',
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return data;
}

/**
 * Update gateway
 */
export async function updateGateway(id: string, payload: GatewayPayload): Promise<Gateway> {
  const { data } = await requestGatewaysApi<Gateway>(
    `${GATEWAYS_ENDPOINT}/${id}`,
    'Failed to update gateway',
    {
      method: 'PUT',
      body: JSON.stringify(payload)
    }
  );
  return data;
}

/**
 * Delete gateway
 */
export async function deleteGateway(id: string | number): Promise<void> {
  await requestGatewaysApi<null>(
    `${GATEWAYS_ENDPOINT}/${id}`,
    'Failed to delete gateway',
    { method: 'DELETE' }
  );
}

/**
 * Fetch SIP profiles for gateways
 */
export async function fetchSipProfiles(): Promise<GatewaySipProfile[]> {
  const { data } = await requestGatewaysApi<GatewaySipProfile[]>(
    `${SIP_PROFILES_ENDPOINT}?limit=100`,
    'Failed to fetch SIP profiles'
  );
  return Array.isArray(data) ? data : [];
}

/**
 * Reload gateway in FreeSWITCH
 */
export async function reloadGateway(id: string | number): Promise<{ success: boolean; message: string; output?: string }> {
  const { data } = await requestGatewaysApi<{ success: boolean; message: string; output?: string }>(
    `${GATEWAYS_ENDPOINT}/${id}/reload`,
    'Failed to reload gateway',
    {
      method: 'POST',
      body: JSON.stringify({})
    }
  );
  return data;
}

/**
 * Stop gateway in FreeSWITCH
 */
export async function stopGateway(id: string | number): Promise<{ success: boolean; message: string; output?: string }> {
  const { data } = await requestGatewaysApi<{ success: boolean; message: string; output?: string }>(
    `${GATEWAYS_ENDPOINT}/${id}/stop`,
    'Failed to stop gateway',
    {
      method: 'POST',
      body: JSON.stringify({})
    }
  );
  return data;
}

/**
 * Fetch real-time gateway status from FreeSWITCH
 */
export async function fetchGatewayStatus(id: string | number): Promise<GatewayStatus> {
  const { data } = await requestGatewaysApi<GatewayStatus>(
    `${GATEWAYS_ENDPOINT}/${id}/status`,
    'Failed to fetch gateway status'
  );
  return data;
}

/**
 * Test gateway connection
 */
export async function testGatewayConnection(id: string | number): Promise<{
  status: string;
  message: string;
  gateway: { name: string; proxy: string | null; realm: string | null };
  details: GatewayStatus;
}> {
  const { data } = await requestGatewaysApi<{
    status: string;
    message: string;
    gateway: { name: string; proxy: string | null; realm: string | null };
    details: GatewayStatus;
  }>(
    `${GATEWAYS_ENDPOINT}/${id}/test`,
    'Failed to test gateway',
    {
      method: 'POST',
      body: JSON.stringify({})
    }
  );
  return data;
}
